import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { DailyCalculatorProvider } from "./DailyCalculatorProvider"
import { CalculatorForm } from "./components/CalculatorForm"
import { ResultsPanel } from "./components/ResultsPanel"
import { StatisticsPanel } from "@/shared/components/statistics"
import { getPeriodDescriptorKey } from "./utils"
import { useDailyCalculator } from "./useDailyCalculator"

function DailyPageContent() {
  const {
    advancedOpen,
    formErrors,
    formValues,
    handleFieldChange,
    handleSubmit,
    isPending,
    lastResultContext,
    result,
    resultsVisible,
    status,
    toggleAdvanced,
  } = useDailyCalculator()
  const { t } = useTranslation()

  const validationAnnouncement = useMemo(() => {
    switch (status) {
      case "errors":
        return t("form.messages.fixErrors")
      case "calculating":
        return t("form.messages.calculating")
      case "complete":
        return t("form.messages.complete")
      default:
        return ""
    }
  }, [status, t])

  const isCalculating = status === "calculating" || isPending

  useEffect(() => {
    if (!resultsVisible || status !== "complete") {
      return
    }

    const resultsSection = document.getElementById("results")

    if (resultsSection instanceof HTMLElement) {
      resultsSection.scrollIntoView({ behavior: "smooth", block: "start" })

      // Focus on the heading for accessibility after scrolling
      const heading = document.getElementById("results-heading")
      if (heading instanceof HTMLElement) {
        heading.focus({ preventScroll: true })
      }
    }
  }, [resultsVisible, status, result])

  const lastInput = lastResultContext?.input ?? null
  const currentPeriodDescriptorKey =
    lastResultContext?.periodDescriptorKey ?? getPeriodDescriptorKey(formValues.period)

  return (
    <div className="mx-auto flex w-full flex-col gap-10 pr-2">
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)]">
        <div className="stack-lg" id="calculator-form">
          <CalculatorForm
            advancedOpen={advancedOpen}
            errors={formErrors}
            isCalculating={isCalculating}
            onFieldChange={handleFieldChange}
            onSubmit={handleSubmit}
            onToggleAdvanced={toggleAdvanced}
            validationAnnouncement={validationAnnouncement}
            values={formValues}
          />
        </div>

        <StatisticsPanel className="lg:sticky lg:top-10" />
      </div>

      {resultsVisible ? (
        <section className="stack-lg pb-16" id="results">
          <h2 className="sr-only">{t("results.heading")}</h2>

          <ResultsPanel
            input={lastInput}
            periodDescriptorKey={currentPeriodDescriptorKey}
            result={result}
            status={status}
          />
        </section>
      ) : null}
    </div>
  )
}

export default function DailyPage() {
  return (
    <DailyCalculatorProvider>
      <DailyPageContent />
    </DailyCalculatorProvider>
  )
}
